import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { EmojiEvents as TrophyIcon, MovieFilter as MovieFilterIcon, Stars as StarsIcon } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useProgressiveImage } from '../../hook/useProgressiveLoad';
import { EsBackButton } from '../../components/EsButtons/EsBackButton';
import { withAlpha } from '../../components/ContestDetailsV2/utils';
import { formatJudgingStatusLabel } from '../../components/ScreenContainer/ContestContainer/Tabs/submissionObsidianShared';

type ManageSubmissionHeroProps = {
    imageUrl?: string;
    title: string;
    contestTitle?: string;
    category?: string;
    awardLabel?: string;
    paymentDue?: boolean;
    judgingStatus?: string;
    submittedAt?: string;
    description?: string;
    onBack?: () => void;
};

const formatSubmittedAt = (value?: string) => {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

export const ManageSubmissionHero: React.FC<ManageSubmissionHeroProps> = ({
    imageUrl,
    title,
    contestTitle,
    category,
    awardLabel,
    paymentDue,
    judgingStatus,
    submittedAt,
    description,
    onBack,
}) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const { sourceLoaded } = useProgressiveImage(imageUrl || '');
    const submittedLabel = formatSubmittedAt(submittedAt);
    const statusLabel = formatJudgingStatusLabel(judgingStatus);
    const shortDescription =
        description && description.trim().length > 0
            ? description.trim().length > 140
                ? `${description.trim().slice(0, 140).trim()}…`
                : description.trim()
            : undefined;

    return (
        <Box
            sx={{
                position: 'relative',
                height: { xs: 440, md: 480 },
                width: '100%',
                borderRadius: 0,
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                overflow: 'hidden',
            }}>
            {sourceLoaded ? (
                <Box
                    component="img"
                    src={sourceLoaded}
                    alt={title}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'brightness(0.75)',
                        transition: 'transform 0.7s',
                        '&:hover': { transform: 'scale(1.05)' },
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(135deg, ${withAlpha(t.highlight, 0.19)} 0%, ${t.pure_black} 100%)`,
                    }}
                />
            )}

            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 0%, ${t.pure_black} 100%)`,
                }}
            />

            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    p: { xs: 2, md: 3 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}>
                <EsBackButton handleDetails={onBack} />
                {(awardLabel || paymentDue) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                        }}>
                        {paymentDue && (
                            <Chip
                                label="Payment Due"
                                size="small"
                                sx={{
                                    bgcolor: withAlpha('#f59e0b', 0.16),
                                    border: `1px solid ${withAlpha('#f59e0b', 0.35)}`,
                                    color: '#fbbf24',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}
                            />
                        )}
                        {awardLabel && (
                            <Chip
                                label={awardLabel}
                                size="small"
                                icon={
                                    <TrophyIcon
                                        sx={{ fill: `${t.highlight} !important`, fontSize: '14px !important' }}
                                    />
                                }
                                sx={{
                                    bgcolor: withAlpha(t.highlight, 0.13),
                                    border: `1px solid ${withAlpha(t.highlight, 0.25)}`,
                                    color: t.highlight,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}
                            />
                        )}
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: { xs: 3, md: 4 },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {contestTitle && (
                        <Chip
                            label={contestTitle}
                            size="small"
                            icon={
                                <MovieFilterIcon
                                    sx={{ fill: `${t.highlight} !important`, fontSize: '14px !important' }}
                                />
                            }
                            sx={{
                                bgcolor: withAlpha(t.highlight, 0.1),
                                border: `1px solid ${withAlpha(t.highlight, 0.22)}`,
                                color: t.true_white,
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                maxWidth: '100%',
                                '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        />
                    )}
                    {category && (
                        <Chip
                            label={category}
                            size="small"
                            icon={<StarsIcon sx={{ fill: `${t.highlight} !important`, fontSize: '14px !important' }} />}
                            sx={{
                                bgcolor: withAlpha(t.btnCustomColor || t.highlight, 0.12),
                                border: `1px solid ${withAlpha(t.btnCustomColor || t.highlight, 0.25)}`,
                                color: t.btnCustomColor || t.highlight,
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                maxWidth: '100%',
                                '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        />
                    )}
                </Box>

                <Typography
                    sx={{
                        fontSize: { xs: 32, md: 52 },
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: t.true_white,
                        letterSpacing: '-0.04em',
                        lineHeight: 1.05,
                        wordBreak: 'break-word',
                        mb: shortDescription || statusLabel || submittedLabel ? 2 : 0,
                    }}>
                    {title}
                    <Box component="span" sx={{ color: t.highlight }}>
                        .
                    </Box>
                </Typography>

                {shortDescription && (
                    <Typography
                        sx={{
                            mb: 2.5,
                            maxWidth: 720,
                            color: withAlpha(t.true_white, 0.82),
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: { xs: 13, md: 15 },
                            lineHeight: 1.45,
                        }}>
                        {shortDescription}
                    </Typography>
                )}

                {(statusLabel || submittedLabel) && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: { xs: 3, md: 5 },
                            alignItems: 'flex-end',
                        }}>
                        {statusLabel && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.2em',
                                        fontWeight: 700,
                                        color: t.true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Status
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 18, md: 22 },
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: t.true_white,
                                        textTransform: 'capitalize',
                                    }}>
                                    {statusLabel}
                                </Typography>
                            </Box>
                        )}
                        {submittedLabel && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.2em',
                                        fontWeight: 700,
                                        color: t.true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Submitted
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 18, md: 22 },
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: t.true_white,
                                    }}>
                                    {submittedLabel}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};
